/**
 * Overfitting Detection tab — Performance Lab.
 * Composite risk score + per-detector cards + concentration tables.
 * Every detector honestly surfaces Unavailable / Insufficient Evidence states.
 */
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Loader2, ShieldAlert, RefreshCw, Info, AlertTriangle, CheckCircle2, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SEVERITY_TONE = {
  "Low Risk":         "border-signal-green/60 text-signal-green",
  "Mild Risk":        "border-signal-yellow/60 text-signal-yellow",
  "Moderate Risk":    "border-signal-yellow/60 text-signal-yellow",
  "High Risk":        "border-signal-red/60 text-signal-red",
  "Critical Risk":    "border-signal-red text-signal-red",
  "Insufficient Evidence": "border-border/60 text-muted-foreground",
  "Unavailable":      "border-border/60 text-muted-foreground italic",
};

const STATUS_TONE = {
  "Passed":              "text-signal-green",
  "Watch":               "text-signal-yellow",
  "Warning":             "text-signal-yellow",
  "High Risk":           "text-signal-red",
  "Critical Risk":       "text-signal-red",
  "Insufficient Evidence": "text-muted-foreground",
  "Unavailable":         "text-muted-foreground italic",
};

const DETECTOR_LABELS = {
  training_to_forward_decay: "Training → Forward Decay",
  profit_concentration:      "Profit Concentration",
  symbol_concentration:      "Symbol Concentration",
  sector_concentration:      "Sector Concentration",
  regime_dependence:         "Regime Dependence",
  model_drift:               "Model Drift",
  prompt_drift:              "Prompt Drift",
  strategy_drift:            "Strategy Drift",
  parameter_sensitivity:     "Parameter Sensitivity",
  small_sample_inflation:    "Small-Sample Inflation",
};

const DETECTOR_HELP = {
  training_to_forward_decay: "Compares in-sample training metrics to forward-test metrics from Walk-Forward Validation. Large degradation implies overfit.",
  profit_concentration:      "Flags when a small number of trades drive most of the profit. Removes top winners to test resilience.",
  symbol_concentration:      "Flags when one or a few symbols dominate profits. Reports profit-factor excluding the dominant symbol(s).",
  sector_concentration:      "Distinguishes portfolio-level from performance-level sector concentration.",
  regime_dependence:         "Detects whether performance is broadly robust vs. accidentally regime-dependent vs. regime-specific by design.",
  model_drift:               "Compares performance before/after the latest AI model change. Does not attribute causation automatically.",
  prompt_drift:              "Compares performance before/after the latest prompt-version change.",
  strategy_drift:            "Compares performance before/after the latest strategy-rule change.",
  parameter_sensitivity:     "Flags high sensitivity to small parameter changes. Requires comparable parameter runs.",
  small_sample_inflation:    "Downgrades confidence for small samples, few regimes, or short paper-trading history.",
};

function pctStr(v, d = 1) {
  if (v == null || !isFinite(v)) return "N/A";
  return `${v.toFixed(d)}%`;
}
function usdStr(v) {
  if (v == null || !isFinite(v)) return "N/A";
  const abs = Math.abs(v), sign = v < 0 ? "-" : "";
  if (abs >= 1000) return `${sign}$${(abs/1000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(2)}`;
}
function num(v, d = 2) {
  if (v == null || !isFinite(v)) return "N/A";
  return v.toFixed(d);
}

function CompositeCard({ composite, sampleSize, onRecompute, recomputing }) {
  if (!composite) return null;
  const score = composite.risk_score;
  const status = composite.status || "Insufficient Evidence";
  const tone = SEVERITY_TONE[status] || SEVERITY_TONE["Insufficient Evidence"];
  return (
    <div className="grid-cell !p-4 space-y-3" data-testid="of-composite">
      <div className="flex items-center gap-3 flex-wrap">
        <ShieldAlert size={16} className="text-signal-red"/>
        <div className="mono uppercase text-[10px] tracking-widest text-muted-foreground">Composite Overfitting Risk</div>
        <div className={`chip !text-[10px] uppercase ${tone}`} data-testid="of-status">{status}</div>
        <div className="ml-auto">
          <button className="btn-outline" onClick={onRecompute} disabled={recomputing} data-testid="of-recompute">
            {recomputing ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>}
            Recompute
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-border/60">
        <div className="p-3 bg-card/40">
          <div className="label-cap">Risk Score</div>
          <div className={`mono text-2xl ${SEVERITY_TONE[status]?.split(" ").find((c) => c.startsWith("text-")) || ""}`}
               data-testid="of-score">
            {score == null ? "—" : `${score.toFixed(1)} / 100`}
          </div>
          {composite.insufficient_data && (
            <div className="text-[10px] mono uppercase text-muted-foreground mt-1">{composite.reason}</div>
          )}
        </div>
        <div className="p-3 bg-card/40">
          <div className="label-cap">Confidence</div>
          <div className="mono text-sm">{composite.confidence_tier || "—"}</div>
        </div>
        <div className="p-3 bg-card/40">
          <div className="label-cap">Sample Maturity</div>
          <div className="mono text-sm">{composite.sample_maturity || "—"}</div>
        </div>
        <div className="p-3 bg-card/40">
          <div className="label-cap">Trades</div>
          <div className="mono text-sm">{sampleSize ?? 0}</div>
        </div>
        <div className="p-3 bg-card/40">
          <div className="label-cap">Detectors Available</div>
          <div className="mono text-sm">{(composite.available_detectors || []).length} / {(composite.available_detectors || []).length + (composite.missing_detectors || []).length}</div>
        </div>
        <div className="p-3 bg-card/40">
          <div className="label-cap">Data Quality</div>
          <div className="mono text-sm">{composite.data_quality_status || "—"}</div>
        </div>
      </div>
      {composite.missing_detectors?.length > 0 && (
        <div className="text-[11px] text-muted-foreground flex items-start gap-2">
          <HelpCircle size={12} className="mt-0.5 shrink-0"/>
          <span>Missing detectors: {composite.missing_detectors.map((k) => DETECTOR_LABELS[k] || k).join(", ")}. Composite renormalises across available detectors only.</span>
        </div>
      )}
      <div className="text-[10px] mono uppercase text-muted-foreground">Calc version: {composite.weight_version || "—"}</div>
    </div>
  );
}

function ConcentrationTable({ rows, groupKey }) {
  if (!rows?.length) return null;
  return (
    <div className="overflow-auto border border-border/40">
      <table className="w-full text-xs">
        <thead className="bg-card/60 text-[10px] uppercase mono text-muted-foreground tracking-widest">
          <tr>
            <th className="p-2 text-left">{groupKey}</th>
            <th className="p-2 text-right">Trades</th>
            <th className="p-2 text-right">Gross P&L</th>
            <th className="p-2 text-right">% of Total</th>
            <th className="p-2 text-right">Win Rate</th>
            <th className="p-2 text-right">PF</th>
            <th className="p-2 text-right">Expectancy</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[groupKey]} className="border-t border-border/30">
              <td className="p-2 mono">{r[groupKey] || "—"}</td>
              <td className="p-2 mono text-right">{r.trade_count}</td>
              <td className={`p-2 mono text-right ${r.gross_pnl >= 0 ? "pnl-up" : "pnl-down"}`}>{usdStr(r.gross_pnl)}</td>
              <td className="p-2 mono text-right">{pctStr(r.pct_of_total, 1)}</td>
              <td className="p-2 mono text-right">{pctStr(r.win_rate_pct, 0)}</td>
              <td className="p-2 mono text-right">{num(r.profit_factor)}</td>
              <td className={`p-2 mono text-right ${r.expectancy >= 0 ? "pnl-up" : "pnl-down"}`}>{usdStr(r.expectancy)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DetectorCard({ dkey, finding }) {
  const label = DETECTOR_LABELS[dkey] || dkey;
  const status = finding.status;
  const tone = STATUS_TONE[status] || STATUS_TONE["Insufficient Evidence"];
  const [expanded, setExpanded] = useState(false);
  const unavailable = status === "Unavailable" || status === "Insufficient Evidence";
  const risk = finding.risk_score ?? 0;
  const barTone =
    status === "Critical Risk" || status === "High Risk" ? "bg-signal-red" :
    status === "Warning" || status === "Watch" ? "bg-signal-yellow" :
    status === "Passed" ? "bg-signal-green" : "bg-border";

  return (
    <div className={`grid-cell !p-3 space-y-2 ${unavailable ? "opacity-70" : ""}`}
         data-testid={`of-detector-${dkey}`}>
      <div className="flex items-center gap-2">
        <span className="mono text-sm">{label}</span>
        <span className="text-muted-foreground/60 hover:text-foreground" title={DETECTOR_HELP[dkey]}>
          <Info size={11}/>
        </span>
        <span className={`chip !text-[9px] uppercase ml-auto ${SEVERITY_TONE[finding.severity] || ""}`}
              data-testid={`of-detector-${dkey}-status`}>{status}</span>
      </div>
      <div className="grid grid-cols-3 gap-px bg-border/40 text-[10px]">
        <div className="p-2 bg-card/40">
          <div className="label-cap">Risk</div>
          <div className={`mono text-sm ${tone}`}>{unavailable ? "N/A" : risk.toFixed(1)}</div>
        </div>
        <div className="p-2 bg-card/40">
          <div className="label-cap">Trades</div>
          <div className="mono text-sm">{finding.sample_size ?? 0}</div>
        </div>
        <div className="p-2 bg-card/40">
          <div className="label-cap">Data Quality</div>
          <div className="mono text-[11px]">{finding.data_quality}</div>
        </div>
      </div>
      {!unavailable && (
        <div className="h-1.5 bg-secondary/40 relative overflow-hidden">
          <div className={`absolute inset-y-0 left-0 transition-all duration-300 ${barTone}`} style={{ width: `${risk}%` }}/>
        </div>
      )}
      {finding.evidence?.length > 0 && (
        <ul className="text-[11px] text-signal-red space-y-0.5">
          {finding.evidence.slice(0, 3).map((e, i) => (
            <li key={i} className="flex items-start gap-1"><AlertTriangle size={10} className="mt-0.5 shrink-0"/>{e}</li>
          ))}
        </ul>
      )}
      {finding.contradictory?.length > 0 && (
        <ul className="text-[11px] text-signal-green space-y-0.5">
          {finding.contradictory.slice(0, 2).map((e, i) => (
            <li key={i} className="flex items-start gap-1"><CheckCircle2 size={10} className="mt-0.5 shrink-0"/>{e}</li>
          ))}
        </ul>
      )}
      {unavailable && finding.reason && (
        <div className="text-[11px] text-muted-foreground italic">{finding.reason}</div>
      )}
      {finding.metrics?.rows?.length > 0 && (
        <>
          <button className="text-[10px] mono uppercase text-[#0A84FF] hover:underline"
                  onClick={() => setExpanded((v) => !v)}
                  data-testid={`of-detector-${dkey}-expand`}>
            {expanded ? "Hide details" : "Show details"}
          </button>
          {expanded && (
            <ConcentrationTable rows={finding.metrics.rows}
                                 groupKey={dkey.includes("symbol") ? "symbol"
                                            : dkey.includes("sector") ? "sector"
                                            : dkey.includes("regime") ? "regime"
                                            : dkey.includes("model") ? "ai_model"
                                            : dkey.includes("prompt") ? "prompt_version"
                                            : dkey.includes("strategy") ? "strategy" : "group"}/>
          )}
          {finding.metrics?.note && (
            <div className="text-[10px] text-muted-foreground italic mt-1">{finding.metrics.note}</div>
          )}
        </>
      )}
    </div>
  );
}

export default function OverfittingSection() {
  const [recomputing, setRecomputing] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["overfitting", "latest"],
    queryFn: async () => {
      try {
        return (await api.get("/overfitting/latest")).data;
      } catch (e) {
        if (e?.response?.status === 404) return null;
        throw e;
      }
    },
  });

  const compute = useMutation({
    mutationFn: async (body) => (await api.post("/overfitting/compute", body || {})).data,
    onSuccess: () => { refetch(); toast.success("Overfitting report updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Compute failed"),
    onSettled: () => setRecomputing(false),
  });

  async function onRecompute() {
    setRecomputing(true);
    compute.mutate({});
  }

  if (isLoading) {
    return (
      <div className="text-xs text-muted-foreground" data-testid="of-loading">
        <Loader2 className="inline animate-spin mr-2" size={12}/>Loading overfitting report…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="grid-cell !p-4 flex items-center gap-2 text-xs" data-testid="of-empty">
        <Info size={14}/>
        <span>No overfitting report has been computed for this workspace yet.</span>
        <button className="btn-primary ml-auto" onClick={onRecompute} disabled={recomputing} data-testid="of-first-run">
          {recomputing ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>}
          Compute Now
        </button>
      </div>
    );
  }

  const detectors = data.detectors || {};
  const composite = data.composite || {};
  const sampleSize = data.sample_size ?? 0;

  return (
    <div className="space-y-4" data-testid="of-section">
      <CompositeCard composite={composite} sampleSize={sampleSize} onRecompute={onRecompute} recomputing={recomputing}/>
      <div className="text-[10px] mono uppercase text-muted-foreground">
        Last computed: {new Date(data.computed_at).toLocaleString()}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(DETECTOR_LABELS).map(([k]) => (
          <DetectorCard key={k} dkey={k} finding={detectors[k] || { status: "Unavailable", severity: "Unavailable" }}/>
        ))}
      </div>
    </div>
  );
}
